import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PrintBlackIcon from "../../assets/icons/PrintBlackIcon";
import XIcon from "../../assets/icons/XIcon";
import "../../style/orderView.css";
import "../../style/orders.css";
import {
  getOrderDetail,
  updateOrderStatus,
  updateOrderItem,
} from "../../services/orders-api";
import { refreshOrdersPendingCount } from "../../utils/ordersPendingCount";

import MinusIconBlack from "../../assets/minus-icon-black.svg";
import PlusIcon from "../../assets/plus-icon.svg";
import CancelModal from "./components/CancelModal";
import ProductConfirmationModal from "./components/ProductConfirmationModal";
import OutOfStockModal from "./components/OutOfStockModal";

import { useAppSelector } from "../../store/hooks";

function OrderView() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showProductConfirmationModal, setShowProductConfirmationModal] =
    useState(false);
  const [showOutOfStockModal, setShowOutOfStockModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [orderData, setOrderData] = useState(null);
  const { isPartsURL, isAccessoriesURL } = useAppSelector(
    (state) => state.domain
  );

  useEffect(() => {
    const fetchOrderDetail = async () => {
      try {
        setLoading(true);
        const data = await getOrderDetail(id);
        setOrderData(data);
      } catch (error) {
        console.error("Error fetching order:", error);
        toast.error("Failed to fetch order details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrderDetail();
    }
  }, [id]);

  const handleQuantityChange = async (itemId, delta) => {
    const item = orderData.items.find((i) => i.id === itemId);
    if (!item) return;

    const newQuantity = Math.max(1, item.quantity + delta);

    try {
      await updateOrderItem(id, itemId, { quantity: newQuantity });
      setOrderData((prev) => ({
        ...prev,
        items: prev.items.map((i) => {
          if (i.id === itemId) {
            return {
              ...i,
              quantity: newQuantity,
              line_total: parseFloat(i.unit_price) * newQuantity,
            };
          }
          return i;
        }),
      }));
      toast.success("Quantity updated successfully");
    } catch (error) {
      console.error("Error updating quantity:", error);
      toast.error("Failed to update quantity");
    }
  };

  // Check if order is shipped (order status is "shipped")
  const isOrderShipped = orderData?.status === "shipped";

  // Check if order is cancelled (order status is "cancelled")
  const isOrderCancelled = orderData?.status === "cancelled";

  // Disable order-level actions if order is shipped OR cancelled
  const isOrderLocked = isOrderShipped || isOrderCancelled;

  const handlePrint = () => {
    const targetId = id || orderData.orderNumber || "print";
    navigate(`/orders/${targetId}/print`);
  };

  const handleCancelOrder = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    try {
      await updateOrderStatus(id, "cancelled");
      toast.success("Order cancelled successfully");
      refreshOrdersPendingCount();
      navigate("/orders");
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error("Failed to cancel order");
    }
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
  };

  const handleShipped = async () => {
    try {
      await updateOrderStatus(id, "shipped");
      toast.success("Order marked as shipped");
      setOrderData((prev) => ({ ...prev, status: "shipped" }));
      refreshOrdersPendingCount();
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to mark order as shipped");
    }
  };

  const handleConfirmOrder = (item) => {
    setSelectedItem(item);
    setShowProductConfirmationModal(true);
  };

  const handleOutOfStock = (item) => {
    setSelectedItem(item);
    setShowOutOfStockModal(true);
  };

  const handleProductConfirm = async (item) => {
    try {
      await updateOrderItem(id, item.id, { status: "confirm_order" });
      setOrderData((prev) => ({
        ...prev,
        items: prev.items.map((i) =>
          i.id === item.id ? { ...i, status: "confirm_order" } : i
        ),
      }));
      toast.success("Product confirmed successfully");
    } catch (error) {
      console.error("Error confirming product:", error);
      toast.error("Failed to confirm product");
    } finally {
      setShowProductConfirmationModal(false);
      setSelectedItem(null);
    }
  };

  const handleOutOfStockConfirm = async (item) => {
    try {
      await updateOrderItem(id, item.id, { status: "out_of_stock" });
      setOrderData((prev) => ({
        ...prev,
        items: prev.items.map((i) =>
          i.id === item.id ? { ...i, status: "out_of_stock" } : i
        ),
      }));
      toast.success("Product marked as out of stock");
    } catch (error) {
      console.error("Error marking product as out of stock:", error);
      toast.error("Failed to mark product as out of stock");
    } finally {
      setShowOutOfStockModal(false);
      setSelectedItem(null);
    }
  };

  if (loading) {
    return (
      <div className="order-view-container">
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="order-view-container">
        <div className="text-center p-5">
          <h3>Order not found</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="order-view-container">
      {/* Header */}
      <div className="order-view-header">
        <button
          className={
            isPartsURL
              ? "order-back-button-parts"
              : isAccessoriesURL
              ? "order-back-button-acc"
              : "order-back-button"
          }
          onClick={() => navigate("/orders")}
        >
          ← Back to Orders Lists
        </button>
      </div>

      {/* Order Info Card */}
      <div className="order-info-card">
        <div className="order-info-header">
          <div className="order-customer-info">
            <h2 className="order-customer-name">{orderData.customer_name}</h2>
            <p className="order-meta">
              {orderData.items_count} Items | {orderData.order_code} |{" "}
              <span style={{ fontWeight: "700", color: "" }}>
                €{orderData.total_amount?.toFixed(2)}
              </span>
            </p>
          </div>
          <div className="order-actions">
            <button className="order-btn-secondary" onClick={handlePrint}>
              <PrintBlackIcon />
              Print
            </button>
            <button
              className={`order-btn-cancel ${
                isOrderLocked ? "order-btn-disabled" : ""
              }`}
              onClick={handleCancelOrder}
              disabled={isOrderLocked}
            >
              <XIcon />
              Cancel Order
            </button>
            <button
              className={`order-btn-shipped ${
                isOrderLocked ? "order-btn-disabled" : ""
              }`}
              onClick={handleShipped}
              disabled={isOrderLocked}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3333 4L6 11.3333L2.66667 8"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Shipped
            </button>
          </div>
        </div>

        {/* Table Header */}
        <div className="order-table">
          <div className="order-table-header">
            <div className="order-col-product">PRODUCT</div>
            <div className="order-col-product">PRICE</div>
            <div className="order-col-product">QTY.</div>
            <div className="order-col-product">TOTAL</div>
            <div className="order-col-confirmation">ORDER CONFIRMATION</div>
          </div>

          {/* Table Body */}
          <div className="order-table-body">
            {orderData.items.map((item) => (
              <div
                key={item.id}
                className={`order-table-row ${
                  item.status === "out_of_stock" ? "out-of-stock-row" : ""
                }`}
              >
                <div className="order-col-product">
                  <div className="order-product-info">
                    {item.product_detail?.image ? (
                      <img
                        src={item.product_detail.image}
                        alt={item.product_detail?.name}
                        className="order-product-image"
                      />
                    ) : (
                      <div className="order-product-placeholder">
                        <span className="order-placeholder-text">
                          {item.product_detail?.name || "Product"}
                        </span>
                      </div>
                    )}
                    <div className="order-product-details">
                      <p className="order-product-name">
                        {isAccessoriesURL && item.product_detail?.unique_identifier_identifier && (
                          <span>{item.product_detail.unique_identifier_identifier} - </span>
                        )}
                        {item.product_detail?.name}
                        {item.product_detail?.suffix &&
                          ` ${item.product_detail.suffix}`}
                      </p>
                      <p
                        className="order-product-name"
                        style={{
                          fontSize: "12px",
                          fontWeight: "400",
                          marginTop: "-4px",
                          color: "#8C959F",
                        }}
                      >
                        {[
                          item.product_detail?.ram,
                          item.product_detail?.storage,
                          ...(item.product_detail?.variants?.map((v) =>
                            typeof v === "object"
                              ? Object.values(v).join(" ")
                              : v
                          ) || []),
                          item.product_detail?.used_mobile ? "Used" : "New",
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      </p>
                      <span
                        className={` ${
                          isPartsURL
                            ? "order-condition-badge-parts"
                            : isAccessoriesURL
                            ? "order-condition-badge-acc"
                            : "order-condition-badge"
                        } ${item.product_detail?.used_mobile ? "used" : "new"}`}
                      >
                        {item.product_detail?.used_mobile ? "Used" : "New"}
                      </span>
                      {item.status === "out_of_stock" && (
                        <div className="order-out-of-stock-info">
                          <p className="order-not-included-text">
                            <span className={`${isPartsURL ? "order-removed-from-inventory-parts" : isAccessoriesURL ? "order-removed-from-inventory-acc" : "order-removed-from-inventory"}`}>
                              Removed from inventory
                            </span>
                            Not included in total.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="order-col-price">
                  €{parseFloat(item.unit_price).toFixed(2)}
                </div>

                {/* quantity column */}
                <div className="order-col-qty-container">
                  <div className="order-quantity-control">
                    <button
                      className={`order-qty-btn ${
                        item.status !== "placed" ? "order-btn-disabled" : ""
                      }`}
                      onClick={() => handleQuantityChange(item.id, -1)}
                      disabled={item.status !== "placed"}
                    >
                      <img src={MinusIconBlack} alt="Minus" />
                    </button>
                    <span className="order-qty-value">{item.quantity}</span>
                    <button
                      className={`order-qty-btn ${
                        item.status !== "placed" ? "order-btn-disabled" : ""
                      }`}
                      onClick={() => handleQuantityChange(item.id, 1)}
                      disabled={item.status !== "placed"}
                    >
                      <img src={PlusIcon} alt="Plus" />
                    </button>
                  </div>
                </div>

                <div className="order-col-total">
                  €{item.line_total?.toFixed(2)}
                </div>
                <div className="order-col-confirmation">
                  {item.status === "pending" && (
                    <>
                      <button
                        className={`${
                          isPartsURL
                            ? "order-btn-confirm-parts order-btn-disabled-parts"
                            : isAccessoriesURL
                            ? "order-btn-confirm-acc order-btn-disabled-acc"
                            : "order-btn-confirm order-btn-disabled"
                        }`}
                        disabled
                      >
                        Confirm Order
                      </button>
                      <button
                        className="order-btn-out-of-stock order-btn-disabled"
                        disabled
                      >
                        Out of Stock
                      </button>
                    </>
                  )}
                  {item.status === "confirm_order" && (
                    <>
                      <button
                        className={`${
                          isPartsURL
                            ? "order-btn-confirm-parts order-btn-disabled-parts"
                            : isAccessoriesURL
                            ? "order-btn-confirm-acc order-btn-disabled-acc"
                            : "order-btn-confirm order-btn-disabled"
                        }`}
                        disabled
                      >
                        Confirmed
                      </button>
                      <button
                        className="order-btn-out-of-stock order-btn-disabled"
                        disabled
                      >
                        Out of Stock
                      </button>
                    </>
                  )}
                  {item.status === "out_of_stock" && (
                    <>
                      <button
                        className={`${
                          isPartsURL
                            ? "order-btn-confirm-parts order-btn-disabled-parts"
                            : isAccessoriesURL
                            ? "order-btn-confirm-acc order-btn-disabled-acc"
                            : "order-btn-confirm order-btn-disabled"
                        }`}
                        disabled
                      >
                        Confirm Order
                      </button>
                      <button
                        className="order-btn-marked-out-of-stock order-btn-disabled"
                        style={{ height: "40px" }}
                        disabled
                      >
                        Marked as Out of Stock
                      </button>
                    </>
                  )}
                  {item.status === "placed" && (
                    <>
                      <button
                        className={`${
                          isPartsURL
                            ? "order-btn-confirm-parts order-btn-disabled-parts"
                            : isAccessoriesURL
                            ? "order-btn-confirm-acc order-btn-disabled-acc"
                            : "order-btn-confirm order-btn-disabled"
                        }`}
                        onClick={() => handleConfirmOrder(item)}
                      >
                        Confirm Order
                      </button>
                      <button
                        className="order-btn-out-of-stock"
                        onClick={() => handleOutOfStock(item)}
                      >
                        Out of Stock
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cancel Order Modal */}
      <CancelModal
        isOpen={showCancelModal}
        onClose={handleCancelModalClose}
        onConfirm={handleCancelConfirm}
        orderItem={orderData}
      />

      {/* Product Confirmation Modal */}
      <ProductConfirmationModal
        isOpen={showProductConfirmationModal}
        onClose={() => {
          setShowProductConfirmationModal(false);
          setSelectedItem(null);
        }}
        onConfirm={handleProductConfirm}
        orderItem={selectedItem}
      />

      {/* Out of Stock Modal */}
      <OutOfStockModal
        isOpen={showOutOfStockModal}
        onClose={() => {
          setShowOutOfStockModal(false);
          setSelectedItem(null);
        }}
        onConfirm={handleOutOfStockConfirm}
        orderItem={selectedItem}
      />
    </div>
  );
}

export default OrderView;
